import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Tag, BookOpen } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { SchemaScript, generateBlogSchema, generateBreadcrumbSchema } from '../utils/seoSchema';
import { listBlogPosts } from '../utils/api';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (_e) {
    return iso;
  }
};

const HeyTaxi = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    // Cold-start protection: production backend pods occasionally take
    // 20-35s to respond on the first request after going idle. Mobile
    // browsers / cellular networks drop the connection well before that,
    // which surfaces as "Unable to load posts" to real visitors.
    // We silently retry up to 3 times with a short backoff before showing
    // any error. Loading indicator stays visible the whole time.
    const MAX_ATTEMPTS = 3;
    const BACKOFF_MS = 1500;

    (async () => {
      let lastErr = null;
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const data = await listBlogPosts();
          if (cancelled) return;
          setPosts(Array.isArray(data) ? data : []);
          setError('');
          setLoading(false);
          return;
        } catch (err) {
          lastErr = err;
          console.warn(`Hey Taxi! load attempt ${attempt}/${MAX_ATTEMPTS} failed:`, err?.message || err);
          if (attempt < MAX_ATTEMPTS && !cancelled) {
            await new Promise((r) => setTimeout(r, BACKOFF_MS * attempt));
          }
        }
      }
      if (!cancelled) {
        console.error('Failed to load blog posts after retries:', lastErr);
        setError('Unable to load posts right now. Please try again shortly.');
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <SEOHead
        title="Hey Taxi! — Taxi-98 Blog: Nassau Travel Tips & Service Updates"
        description="Travel tips, Taxi-98 updates, Nassau guides, and local insight from the road. Practical advice for airport transfers, cruise port pickups, tours, and more."
        canonical="/hey-taxi"
      />
      <SchemaScript schema={generateBlogSchema(posts)} />
      <SchemaScript
        schema={generateBreadcrumbSchema([
          { name: 'Home', url: 'https://taxi-98.com/' },
          { name: 'Hey Taxi!', url: 'https://taxi-98.com/hey-taxi' },
        ])}
      />

      <main className="bg-white min-h-screen" data-testid="hey-taxi-page">
        {/* Hero / header */}
        <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-20 bg-gradient-to-br from-gray-950 via-slate-900 to-gray-950 overflow-hidden">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-cyan-500/15 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-flex items-center gap-2 bg-cyan-400/15 border border-cyan-300/30 text-cyan-200 text-xs font-bold tracking-[0.18em] uppercase px-3 py-1 rounded-full mb-5">
              <BookOpen className="w-3.5 h-3.5" />
              Taxi-98 Journal
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4" data-testid="hey-taxi-heading">
              Hey Taxi!
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Travel tips, Taxi-98 updates, Nassau guides, and local insight from the road.
            </p>
          </div>
        </section>

        {/* Cards */}
        <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading && (
              <p className="text-center text-gray-500 py-16" data-testid="hey-taxi-loading">Loading articles…</p>
            )}
            {!loading && error && (
              <div className="max-w-xl mx-auto bg-red-50 border border-red-200 text-red-700 rounded-lg p-5 text-sm" data-testid="hey-taxi-error">
                {error}
              </div>
            )}
            {!loading && !error && posts.length === 0 && (
              <p className="text-center text-gray-500 py-16" data-testid="hey-taxi-empty">
                No posts yet. Check back soon for new articles.
              </p>
            )}
            {!loading && !error && posts.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="hey-taxi-grid">
                {posts.map((post) => (
                  <article
                    key={post.id}
                    data-testid={`hey-taxi-card-${post.slug}`}
                    className="group bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden"
                  >
                    {/* Image / placeholder */}
                    <div className="relative aspect-[16/9] bg-gradient-to-br from-cyan-100 via-blue-100 to-amber-100 overflow-hidden">
                      {post.imageUrl ? (
                        <img
                          src={post.imageUrl}
                          alt={post.imageAlt || post.title}
                          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                          loading="lazy"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <BookOpen className="w-16 h-16 text-cyan-700/30" />
                        </div>
                      )}
                    </div>

                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                        <span className="inline-flex items-center gap-1 bg-cyan-50 border border-cyan-200 text-cyan-700 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider text-[10px]">
                          <Tag className="w-3 h-3" />
                          {post.category}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(post.publishedAt || post.createdAt)}
                        </span>
                      </div>
                      <h2 className="text-xl font-bold text-gray-900 mb-3 leading-snug group-hover:text-cyan-700 transition-colors">
                        {post.title}
                      </h2>
                      <p className="text-sm text-gray-600 leading-relaxed line-clamp-4 flex-1">
                        {post.excerpt}
                      </p>
                      <Link
                        to={`/hey-taxi/${post.slug}`}
                        data-testid={`hey-taxi-readmore-${post.slug}`}
                        className="mt-5 inline-flex items-center gap-2 text-cyan-700 hover:text-cyan-800 font-semibold text-sm group/cta"
                      >
                        <span>Read More</span>
                        <ArrowRight className="w-4 h-4 group-hover/cta:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
};

export default HeyTaxi;
